import { useToastStore, type ToastVariant } from '@/stores/toast.store';
import { cn } from '@/lib/utils';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

const ICONS: Record<ToastVariant, React.ElementType> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
};

const STYLES: Record<ToastVariant, string> = {
  success: 'border-success/30 bg-success-50 text-success-700 dark:border-success/20 dark:bg-success/10 dark:text-success',
  error: 'border-danger/30 bg-danger-50 text-danger-700 dark:border-danger/20 dark:bg-danger/10 dark:text-danger',
  warning: 'border-warning/30 bg-warning-50 text-warning-700 dark:border-warning/20 dark:bg-warning/10 dark:text-warning',
  info: 'border-primary/30 bg-primary/5 text-primary dark:border-primary/20 dark:bg-primary/10',
};

export function ToastContainer() {
  const { toasts, removeToast } = useToastStore();

  if (toasts.length === 0) return null;

  return (
    <div role="region" aria-label="Notifications" aria-live="polite" className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
      {toasts.map((t) => {
        const Icon = ICONS[t.variant];
        return (
          <div
            key={t.id}
            role="alert"
            className={cn(
              'flex items-start gap-3 rounded-xl border px-4 py-3 shadow-lg animate-fade-up backdrop-blur-sm',
              STYLES[t.variant],
            )}
          >
            <Icon className="h-4.5 w-4.5 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="flex-1 text-sm font-medium">{t.message}</p>
            <button
              onClick={() => removeToast(t.id)}
              aria-label="Dismiss notification"
              className="shrink-0 rounded-md p-0.5 opacity-60 transition-opacity hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
